import moment from 'moment';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import VoiceMemoPlayback from '@treams/treams-app/src/components/voice-memos/VoiceMemoPlayback';
import * as graphql from '@treams/treams-network';
import { Avatar } from '@treams/treams-ui/common/Avatar';
import { Chat } from '@treams/treams-ui/common/Chat';
import { Heading3, Paragraph } from '@treams/treams-ui/common/Text';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';

import CompetencyDisplay from '../../packages/kakapo-ui-application/common/containers/CompetencyDisplay';

const DateLabel = styled(Paragraph)`
	opacity: 0;
	transition: 250ms opacity ease-out;
`;

const AnswerContainer = styled(Flex)`
	page-break-inside: avoid;

	:hover {
		${DateLabel} {
			opacity: 1;
		}
	}
`;

type Props = {
	feedbackScore: graphql.CompetencyFeedbackScore;
	levels: graphql.CompetencyLevelOption[];
	competencyID?: string;
};

function findCompetencyAnswer(
	feedbackResponse: graphql.CompetencyFeedbackScore['feedbackResponse'],
	competencyID: string | undefined,
) {
	const answers = feedbackResponse.answers ?? [];

	if (competencyID && feedbackResponse.questions?.length) {
		const questionIDs = new Set(
			feedbackResponse.questions
				.filter((question) => question.competency?.id === competencyID)
				.map((question) => question.id),
		);
		const matched = answers.find(
			(scoreAnswer) =>
				questionIDs.has(scoreAnswer.questionID) &&
				typeof scoreAnswer.competencyLevelIndex === 'number',
		);

		if (matched) {
			return matched;
		}
	}

	return answers.find((scoreAnswer) => typeof scoreAnswer.competencyLevelIndex === 'number');
}

const GrowthPathReceivedFeedback: FC<Props> = ({ feedbackScore, levels, competencyID }) => {
	const { t } = useTranslation();

	const giver = feedbackScore.giver;
	const giverName = `${giver.firstName} ${giver.lastName}`;

	const answer = findCompetencyAnswer(feedbackScore.feedbackResponse, competencyID);

	return (
		<AnswerContainer>
			<Avatar user={giver} size={32} flexGrow={0} flexShrink={0} />
			<Box ml={2} flexGrow={1} flexShrink={1}>
				<Chat mb={3}>
					<Flex alignItems="center" justifyContent="space-between" mb={2}>
						<Heading3 mb={0}>{giverName}</Heading3>
						<DateLabel>{moment(feedbackScore.timestamp).format('LL')}</DateLabel>
					</Flex>
					{answer?.text && <QuillText value={answer.text} />}
					{typeof feedbackScore.competencyLevelIndex === 'number' && (
						<Box mt={2}>
							<CompetencyDisplay levels={levels} manager={feedbackScore.competencyLevelIndex} />
						</Box>
					)}
					{feedbackScore.competencyLevelIndex === null && (
						<Box mt={2}>
							<Paragraph fontWeight="bold">{t('feedback.answer.not.observed')}</Paragraph>
						</Box>
					)}
					{answer?.voiceMemo && (
						<Box py={2}>
							<VoiceMemoPlayback
								samples={answer.voiceMemo.samples}
								source={answer.voiceMemo.asset.source.url}
							/>
						</Box>
					)}
				</Chat>
			</Box>
		</AnswerContainer>
	);
};

export function GrowthPathReceivedFeedbackSkeleton() {
	return (
		<AnswerContainer>
			<Avatar.Skeleton size={32} flexGrow={0} flexShrink={0} />
			<Chat ml={2} flexGrow={1} flexShrink={1}>
				<Heading3.Skeleton />
				<Paragraph.Skeleton mt={2} />
			</Chat>
		</AnswerContainer>
	);
}

export default GrowthPathReceivedFeedback;
